import { useState, useEffect } from "react";
import { supabase } from "../auth/supabaseClient";
import { AiFillCheckCircle, AiOutlineUpload } from "react-icons/ai";
import { BiUserCircle } from "react-icons/bi";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { StepBar } from "../common/steps";

const ProfilePhoto = ({ url, size, onUpload }) => {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (url) downloadImage(url);
  }, [url]);

  const downloadImage = async (path) => {
    try {
      const { data, error } = await supabase.storage
        .from("avatars")
        .download(path);
      if (error) {
        throw error;
      }
      const url = URL.createObjectURL(data);
      setPhotoUrl(url);
    } catch (error) {
      console.log("Error downloading image: ", error.message);
    }
  };

  const uploadAvatar = async (event) => {
    try {
      setUploading(true);

      if (!event.target.files || event.target.files.length === 0) {
        throw new Error("You must select an image to upload.");
      }

      const file = event.target.files[0];
      const fileExt = file.name.split(".").pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      let { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      onUpload(filePath);
    } catch (error) {
      alert(error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {uploading ? (
        "Uploading..."
      ) : (
        <>
          <div className="px-4 py-5 rounded-lg text-center w-48">
            <div className="relative">
              <img
                className="w-auto mx-auto rounded-full object-cover object-center"
                src={
                  photoUrl
                    ? photoUrl
                    : "https://t3.ftcdn.net/jpg/02/33/46/24/360_F_233462402_Fx1yke4ng4GA8TJikJZoiATrkncvW6Ib.jpg"
                }
                alt="Avatar Upload"
              />
              <label className="cursor-pointer">
                <span className="absolute bottom-0 right-0 px-4 py-2 bg-indigo-500 text-white text-sm rounded-full">
                  <AiOutlineUpload />
                </span>
                <input
                  className="hidden"
                  type="file"
                  id="single"
                  accept="image/*"
                  onChange={uploadAvatar}
                  disabled={uploading}
                />
              </label>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const SetProfile = () => {
  const [loading, setLoading] = useState(true);
  const [successProfileUpdate, setSuccessProfileUpdate] = useState(false);

  const [photoUrl, setPhotoUrl] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [altEmail, setAltEmail] = useState("");
  const [username, setUsername] = useState("");
  const [isProfileSet, setIsProfileSet] = useState(null);

  const { user } = useAuth();

  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      getProfile();
    }
  }, [user]);

  const getProfile = async () => {
    setLoading(true);
    let { data, error, status } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", await user.id)
      .single();

    if (data) {
      setFirstName(data.first_name);
      setLastName(data.last_name);
      setAltEmail(data.alt_email);
      setIsProfileSet(data.profile_set);
      setPhotoUrl(data.photo_url);

      if (data.username != null) {
        setUsername(data.username);
      } else if (data.first_name) {
        // validate first
        let generatedUsername = generateValidUsername(data.first_name);
        // then set
        setUsername(generatedUsername);
      }
    } else {
      console.log(error, status);
      console.log("No previous profile found, creating new profile");
      // generate a random username
      setUsername((Math.random() + 1).toString(36).substring(7));
    }

    setLoading(false);
  };

  const generateValidUsername = (first_name) => {
    let username = first_name.toLowerCase();
    let num = 1;

    while (!validateUsername(username)) {
      username = username + num;
      num++;
    }

    return username;
  };

  const validateUsername = async (username) => {
    let { data, error, status } = await supabase
      .from("profiles")
      .select("username")
      .eq("username", username);

    if (data.length == 0) {
      return true;
    }
    return false;
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    const updates = {
      id: user.id,
      first_name: firstName,
      last_name: lastName,
      alt_email: altEmail,
      username: username,
      profile_set: true,
      updated_at: new Date(),
    };

    let { error } = await supabase.from("profiles").upsert(updates);

    setSuccessProfileUpdate(true);
    setTimeout(() => {
      setSuccessProfileUpdate(false);
    }, 1000);

    navigate("/set-wallet");
  };

  const updatePhotoUrl = async (url) => {
    const updates = {
      id: user.id,
      photo_url: url,
      updated_at: new Date(),
    };

    let { error } = await supabase.from("profiles").upsert(updates);
  };

  return (
    <div>
      <StepBar
        steps={[
          { label: "Profile", status: "current" },
          { label: "Wallet", status: "incomplete" },

          { label: "Team", status: "incomplete" },
          { label: "Plan", status: "incomplete" },
        ]}
      />
      {!loading ? (
        <div className="rounded max-w-xl p-5 bg-slate-100">
          <h1 className="text-2xl font-bold mb-5 flex items-center">
            <BiUserCircle size={40} /> Your Profile
          </h1>

          <div className="flex justify-center ">
            <ProfilePhoto
              className="justify-center"
              url={photoUrl}
              size={150}
              onUpload={(url) => {
                setPhotoUrl(url);
                updatePhotoUrl(url);
              }}
            />
          </div>

          <form onSubmit={handleUpdateProfile}>
            <div className="flex flex-row">
              <div className="mb-4 mr-2">
                <label
                  className="block text-grey-darker text-sm mb-1"
                  htmlFor="XXXXXX"
                >
                  First Name
                </label>
                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-grey-darker"
                  id="firstName"
                  type="text"
                  placeholder="Jane"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>

              <div className="mb-4">
                <label
                  className="block text-grey-darker text-sm mb-1"
                  htmlFor="XXXXXX"
                >
                  Last Name
                </label>
                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-grey-darker"
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            <div className="mb-4">
              <label
                className="block text-grey-darker text-sm mb-1"
                htmlFor="XXXXXX"
              >
                Alternative Email
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-grey-darker"
                id="altEmail"
                type="email"
                placeholder="alternative@ribbon.cool"
                value={altEmail}
                onChange={(e) => setAltEmail(e.target.value)}
              />
            </div>

            <div className="mb-4">
              <label
                className="block text-grey-darker text-sm mb-1"
                htmlFor="XXXXXX"
              >
                Personalized URL
              </label>
              {process.env.REACT_APP_RIBBON_PLATFORM_URL.replace(
                /(^\w+:|^)\/\//,
                ""
              )}
              /p/{username}
              {/* <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-grey-darker"
                id="altEmail"
                type="email"
                placeholder="alternative@ribbon.cool"
                value={altEmail}
                onChange={(e) => setAltEmail(e.target.value)}
              /> */}
            </div>

            {/* <TeamConfigWidget /> */}

            <button
              disabled={loading}
              className="inline-flex items-center justify-center bg-indigo-500 text-white font-bold w-full py-2 px-4 rounded"
            >
              Next
              {successProfileUpdate ? (
                <AiFillCheckCircle className="ml-4" />
              ) : null}
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default SetProfile;
